import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';

// Route guard. Two independent checks:
//  - signed in at all (status), redirecting to /login with the attempted
//    path preserved so a successful login can bounce back here
//  - optionally, a permission name (checked against the current user's flat
//    `permissions` array — see AuthContext.hasPermission) for routes that
//    need more than "any authenticated user".
//
// Kept as the same `permission`-prop shape the target app already used
// pre-migration, since every route in App.tsx is written against it. Pass C
// extends it to accept a pipe-delimited OR-list ("a|b|c") so a frontend
// route can mirror a backend `permission:a|b|c` middleware group exactly
// (see routes/api.php) — this matters for the Revenue/Procurement modules,
// where a self-service role (member/customer/supplier) is granted access
// via a narrow permission like `customer.profile.view` that sits alongside,
// not instead of, the module-wide `revenue.view` etc.
export function RequireAuth({ permission }: { permission?: string }) {
  const { status, hasPermission } = useAuth();
  const location = useLocation();

  if (status === 'idle' || status === 'loading') {
    // Avoids a flash of the login page while the initial /me check (fired
    // on page load / hard refresh) is still in flight.
    return null;
  }

  if (status === 'unauthenticated') {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace state={{ from: location.pathname }} />;
  }

  if (permission && !permission.split('|').some((name) => hasPermission(name))) {
    return (
      <div role="alert">
        <h1>Not authorized</h1>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  return <Outlet />;
}
